"use client"

export const dynamic = 'force-dynamic'

import { Navigation } from "@/components/navigation"
import { Footer } from "@/components/footer"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Scale, FileText, Shield, AlertTriangle } from "lucide-react"

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <Navigation />

      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-muted-foreground">
              Terms and conditions for using our AI-powered localization service
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Last updated: December 2024
            </p>
          </div>

          <div className="space-y-8">
            {/* Acceptance of Terms */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="h-5 w-5 text-blue-600" />
                  Acceptance of Terms
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  By accessing and using Locsy ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service. These Terms apply to all users of the Service, including visitors, registered users, and subscribers.
                </p>
              </CardContent>
            </Card>

            {/* Description of Service */}
            <Card>
              <CardHeader>
                <CardTitle>Description of Service</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">
                  Locsy is a web-based platform that provides AI-powered translation services for JSON localization files. The Service allows users to:
                </p>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>• Upload JSON language files for translation</li>
                  <li>• Translate content into multiple languages using OpenAI's API</li>
                  <li>• Download translated files or access them via API</li>
                  <li>• Manage multiple projects and collaboration</li>
                  <li>• Access translation services through REST API and CLI tools</li>
                </ul>
                <p className="text-muted-foreground text-sm">
                  The Service operates on a "Bring Your Own Key" (BYOK) model, requiring users to provide their own OpenAI API keys for translation processing.
                </p>
              </CardContent>
            </Card>

            {/* User Accounts */}
            <Card>
              <CardHeader>
                <CardTitle>User Accounts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Account Creation</h3>
                  <p className="text-muted-foreground text-sm">
                    To use the Service, you must create an account by providing accurate and complete information. You are responsible for maintaining the confidentiality of your account credentials.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Account Responsibility</h3>
                  <p className="text-muted-foreground text-sm">
                    You are responsible for all activities that occur under your account. You must notify us immediately of any unauthorized use of your account or any other breach of security.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">API Keys</h3>
                  <p className="text-muted-foreground text-sm">
                    You are responsible for obtaining, maintaining, and securing your OpenAI API keys. We are not liable for any costs incurred through your OpenAI API usage.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Acceptable Use */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-green-600" />
                  Acceptable Use
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree NOT to:</p>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>• Use the Service for any illegal or unauthorized purpose</li>
                  <li>• Upload malicious code, viruses, or harmful content</li>
                  <li>• Attempt to gain unauthorized access to our systems</li>
                  <li>• Interfere with or disrupt the Service or servers</li>
                  <li>• Use the Service to translate inappropriate, offensive, or harmful content</li>
                  <li>• Reverse engineer, decompile, or disassemble any part of the Service</li>
                  <li>• Resell or redistribute the Service without authorization</li>
                  <li>• Violate any applicable laws or regulations</li>
                </ul>
              </CardContent>
            </Card>

            {/* Content and Data */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-600" />
                  Content and Data
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Your Content</h3>
                  <p className="text-muted-foreground text-sm">
                    You retain all rights to the content you upload to the Service. By uploading content, you grant us a limited license to process, store, and translate your content solely for the purpose of providing the Service.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Content Responsibility</h3>
                  <p className="text-muted-foreground text-sm">
                    You are solely responsible for the content you upload and translate. You warrant that you have the right to use and translate such content and that it does not violate any third-party rights.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Data Retention</h3>
                  <p className="text-muted-foreground text-sm">
                    We may retain your uploaded files for up to 30 days to provide the Service. You can request deletion of your data at any time through your account settings.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Service Availability */}
            <Card>
              <CardHeader>
                <CardTitle>Service Availability</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">
                  We strive to maintain high availability of the Service, but we do not guarantee uninterrupted access. The Service may be temporarily unavailable due to:
                </p>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>• Scheduled maintenance</li>
                  <li>• Technical issues or server problems</li>
                  <li>• Third-party service outages (including OpenAI)</li>
                  <li>• Force majeure events</li>
                </ul>
                <p className="text-muted-foreground text-sm">
                  We will make reasonable efforts to provide advance notice of scheduled maintenance when possible.
                </p>
              </CardContent>
            </Card>

            {/* Disclaimers */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  Disclaimers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Translation Accuracy</h3>
                  <p className="text-muted-foreground text-sm">
                    While we use advanced AI models for translation, we cannot guarantee 100% accuracy of translations. You should review and verify translations before using them in production environments.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Third-Party Services</h3>
                  <p className="text-muted-foreground text-sm">
                    Our Service relies on third-party APIs (primarily OpenAI). We are not responsible for the availability, performance, or policies of these third-party services.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">No Warranty</h3>
                  <p className="text-muted-foreground text-sm">
                    The Service is provided "as is" without warranties of any kind, either express or implied, including but not limited to merchantability, fitness for a particular purpose, or non-infringement.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Limitation of Liability */}
            <Card>
              <CardHeader>
                <CardTitle>Limitation of Liability</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  To the maximum extent permitted by law, Locsy shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or use, arising out of or relating to your use of the Service, regardless of the theory of liability and whether or not we have been advised of the possibility of such damages.
                </p>
              </CardContent>
            </Card>

            {/* Indemnification */}
            <Card>
              <CardHeader>
                <CardTitle>Indemnification</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  You agree to indemnify, defend, and hold harmless Locsy and its officers, directors, employees, and agents from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees arising out of or relating to your use of the Service, violation of these Terms, or violation of any rights of another.
                </p>
              </CardContent>
            </Card>

            {/* Termination */}
            <Card>
              <CardHeader>
                <CardTitle>Termination</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Termination by You</h3>
                  <p className="text-muted-foreground text-sm">
                    You may terminate your account at any time by contacting us or using the account deletion feature in your settings.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Termination by Us</h3>
                  <p className="text-muted-foreground text-sm">
                    We may terminate or suspend your account immediately, without prior notice, for any reason, including if you breach these Terms.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Effect of Termination</h3>
                  <p className="text-muted-foreground text-sm">
                    Upon termination, your right to use the Service will cease immediately. We may delete your account data according to our data retention policies.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Governing Law */}
            <Card>
              <CardHeader>
                <CardTitle>Governing Law</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  These Terms shall be interpreted and governed by the laws of the State of California, United States, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be resolved in the state or federal courts located in San Francisco County, California.
                </p>
              </CardContent>
            </Card>

            {/* Changes to Terms */}
            <Card>
              <CardHeader>
                <CardTitle>Changes to Terms</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the updated Terms on our website and updating the "Last updated" date. Your continued use of the Service after such changes constitutes acceptance of the new Terms.
                </p>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-4">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="space-y-2 text-muted-foreground text-sm">
                  <p>Email: legal@locsy.ai</p>
                  <p>Address: 123 Tech Street, San Francisco, CA 94102, United States</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
} 